'use client';

import { getBaseImageUrl } from '@/constants/hooks';
import { CategoryData } from '@/services/dataTypes';
import React, { useCallback, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const ACCENT_ORANGE = '#F97316';
const CARD_W = 136;
const GAP = 12;
const SPEED = 0.55;

interface CardProps {
    category: CategoryData;
    companyDomain: string;
    clone?: boolean;
}

const CategoryCard = ({ category, companyDomain, clone }: CardProps) => {
    const offerLabel = category.total_offers
        ? `${category.total_offers} ${category.total_offers === 1 ? 'offer' : 'offers'}`
        : null;

    return (
        <Link
            href={`/${category.url}`}
            tabIndex={clone ? -1 : 0}
            aria-hidden={clone ? true : undefined}
            className="group flex-none no-underline"
            style={{ width: CARD_W }}
            aria-label={clone ? undefined : `Browse ${category.name}${offerLabel ? ` — ${offerLabel}` : ''}`}
        >
            <div
                className="
                    relative rounded-2xl bg-white
                    p-3 flex flex-col items-center gap-2
                    transition-all duration-300 ease-out
                    group-hover:shadow-[0_8px_28px_rgba(249,115,22,0.20)]
                "
                style={{
                    border: '1.5px solid #E5E7EB',
                    transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
                    minHeight: 120,
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = ACCENT_ORANGE; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E7EB'; }}
            >


                {/* Icon box */}
                <div
                    className="
                        w-[48px] h-[48px] rounded-xl flex items-center justify-center shrink-0
                        bg-[#F8FAFC] border border-[#E5E7EB]
                        transition-all duration-300
                        group-hover:bg-[#FFF7ED] group-hover:border-[#F97316]/40
                    "
                >
                    {category.category_image ? (
                        <div className="relative w-7 h-7">
                            <Image
                                src={getBaseImageUrl(companyDomain, category.category_image, '')}
                                alt={category.name}
                                fill
                                className="object-contain transition-transform duration-300 group-hover:scale-110"
                                sizes="28px"
                            />
                        </div>
                    ) : (
                        <span className="text-xl font-extrabold select-none text-[#1E3A5F] group-hover:text-[#F97316] transition-colors duration-300">
                            {category.name?.[0]?.toUpperCase()}
                        </span>
                    )}
                </div>

                {/* Name — fixed 2-line height so all cards stay the same height */}
                <span
                    className="text-[12px] font-semibold text-[#4B5563] text-center leading-snug group-hover:text-[#111827] transition-colors duration-200 line-clamp-2"
                >
                    {category.name}
                </span>

                {/* Offer badge — optional, sits at bottom */}
                {offerLabel && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#EA580C] border border-[#F97316] tracking-wide uppercase shrink-0">
                        {offerLabel}
                    </span>
                )}
            </div>
        </Link>
    );
};

/* ─── Carousel ───────────────────────────────────────────────────── */
interface Props {
    categories: CategoryData[];
    companyDomain: string;
    catSlug: string;
}

const HomeCategoriesCarousel = ({ categories, companyDomain }: Props) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);
    const posRef = useRef(0);
    const pausedRef = useRef(false);
    const manualRef = useRef(false);
    const items = [...categories, ...categories];
    const loopW = (CARD_W + GAP) * categories.length;

    /* ── rAF loop ─────────────────────────────────────────────────── */
    const animate = useCallback(() => {
        if (!pausedRef.current && !manualRef.current) {
            posRef.current += SPEED;
            if (posRef.current >= loopW) posRef.current -= loopW;
            if (rowRef.current) {
                rowRef.current.style.transform = `translateX(${-posRef.current}px)`;
            }
        }
        rafRef.current = requestAnimationFrame(animate);
    }, [loopW]);

    useEffect(() => {
        rafRef.current = requestAnimationFrame(animate);
        return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
    }, [animate]);

    /* ── Arrow navigation ─────────────────────────────────────────── */
    const scrollBy = (dir: 'left' | 'right') => {
        const step = (CARD_W + GAP) * 3;
        let next = posRef.current + (dir === 'right' ? step : -step);
        if (next < 0) next += loopW;
        if (next >= loopW) next -= loopW;

        // Animate manually over ~500 ms
        const start = posRef.current;
        const startTs = performance.now();
        const duration = 500;

        manualRef.current = true;

        const ease = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        const step_ = (now: number) => {
            const elapsed = now - startTs;
            const t = Math.min(elapsed / duration, 1);

            // Handle wrap-around by computing shortest path
            let delta = next - start;
            if (Math.abs(delta) > loopW / 2) {
                delta = delta > 0 ? delta - loopW : delta + loopW;
            }

            let cur = start + delta * ease(t);
            if (cur < 0) cur += loopW;
            if (cur >= loopW) cur -= loopW;

            posRef.current = cur;
            if (rowRef.current) {
                rowRef.current.style.transform = `translateX(${-cur}px)`;
            }

            if (t < 1) {
                requestAnimationFrame(step_);
            } else {
                manualRef.current = false;
            }
        };

        requestAnimationFrame(step_);
    };

    const pause = () => { pausedRef.current = true; };
    const resume = () => { pausedRef.current = false; };

    return (
        <div className="relative py-3">
            {/* Fade masks */}
            <div
                className="pointer-events-none absolute left-0 inset-y-0 w-20 z-10"
                style={{ background: 'linear-gradient(to right, #fff 0%, transparent 100%)' }}
                aria-hidden="true"
            />
            <div
                className="pointer-events-none absolute right-0 inset-y-0 w-20 z-10"
                style={{ background: 'linear-gradient(to left, #fff 0%, transparent 100%)' }}
                aria-hidden="true"
            />

            {/* Arrows */}
            {(['left', 'right'] as const).map(dir => (
                <button
                    key={dir}
                    onClick={() => scrollBy(dir)}
                    aria-label={dir === 'left' ? 'Previous categories' : 'Next categories'}
                    className={`
                        absolute top-1/2 -translate-y-1/2 z-20
                        w-9 h-9 rounded-full bg-white border border-[#E5E7EB]
                        shadow-[0_2px_12px_rgba(30,58,95,0.12)]
                        flex items-center justify-center
                        text-[#1E3A5F] hover:bg-[#F97316] hover:text-white hover:border-[#F97316]
                        transition-all duration-200
                        ${dir === 'left' ? 'left-1' : 'right-1'}
                    `}
                >
                    {dir === 'left' ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 010 1.06L8.06 10l3.72 3.72a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 0z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 010-1.06z" clipRule="evenodd" />
                        </svg>
                    )}
                </button>
            ))}

            {/* Clip container — only hides horizontal overflow */}
            <div style={{ overflow: 'hidden' }}>
                <div
                    ref={rowRef}
                    onMouseEnter={pause}
                    onMouseLeave={resume}
                    onFocus={pause}
                    onBlur={resume}
                    style={{
                        display: 'flex',
                        gap: GAP,
                        willChange: 'transform',
                        transform: 'translateX(0px)',
                    }}
                >
                    {items.map((cat, i) => (
                        <CategoryCard
                            key={`${cat.unique_id ?? i}-${i}`}
                            category={cat}
                            companyDomain={companyDomain}
                            clone={i >= categories.length}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HomeCategoriesCarousel;
